import { useState } from "react";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbodboh653ivfvnqo0";

const SUBJECTS = ["内容纠错", "提供线索", "投稿合作", "其他事项"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    formData.delete("company_alt");
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的信息，我们会在两个工作日内回复。");
      form.reset();
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        const text = String(value).trim();
        if (text === "") return;
        params.append(key, text);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("已收到你的信息，我们会在两个工作日内回复。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试或直接发邮件给我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试或直接发邮件给我们。");
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-xs font-medium text-foreground-600">
            你的称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="如何称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-xs font-medium text-foreground-600">
            邮箱地址
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-600">
          事项类型
        </label>
        <select
          id="contact-subject"
          name="subject"
          required
          defaultValue="内容纠错"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          {SUBJECTS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-xs font-medium text-foreground-600">
          具体说明
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请尽量提供完整信息与原始出处，便于我们核实（500 字以内）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-extra-spacer"
      />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-foreground-400">提交即表示你同意我们为核实内容而使用这些信息。</p>
        <button
          type="submit"
          disabled={status === "loading"}
          className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "loading" ? "提交中…" : "提交信息"}
        </button>
      </div>

      {status !== "idle" && status !== "loading" && (
        <div
          role="status"
          className={`mt-4 flex items-start gap-2 rounded-md border px-3.5 py-2.5 text-sm ${
            status === "success"
              ? "border-secondary-300 bg-secondary-50 text-secondary-800"
              : "border-primary-300 bg-primary-50 text-primary-700"
          }`}
        >
          <i className={`mt-0.5 ${status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}`} aria-hidden="true"></i>
          <span>{message}</span>
        </div>
      )}
    </form>
  );
}